import { HttpContextToken, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';
import { catchError, Observable, switchMap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { SrvsService } from 'src/app/srvs/services/srvs.service';
import { isServiceUrl } from '../services/platform.service';

/** Set on the one retry, so that a token refused twice is not renewed again. */
const SERVICE_TOKEN_RETRIED = new HttpContextToken<boolean>(() => false);

/**
 * A 401 from a service's node, to a request that carried the service token this tab holds for it,
 * means the node no longer accepts that token: it may still look valid here, after the node's
 * signing key changed or with a clock out of step. The token is renewed from the admin once and
 * the request sent again; only if the node refuses the new token too does the 401 reach the page.
 *
 * Without this the 401 reached GlobalRequestErrorHandlerInterceptor, which ended the account
 * session for a token that has nothing to do with it.
 *
 * Provided after GlobalRequestErrorHandlerInterceptor, so it sees the error first.
 */
@Injectable({ providedIn: 'root' })
export class ServiceTokenRetryInterceptor implements HttpInterceptor {
  // SrvsService uses HttpClient, which is built from the interceptors: read lazily
  private injector = inject(Injector);

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: unknown) => {
        const srvId = this.renewable(req, error);
        if (!srvId) {
          return throwError(() => error);
        }

        return this.injector.get(SrvsService).adminJwt(srvId, true).pipe(
          switchMap(token =>
            next.handle(
              req.clone({
                setHeaders: { Authorization: `Bearer ${token}` },
                context: req.context.set(SERVICE_TOKEN_RETRIED, true),
              })
            )
          ),
          // the admin could not issue a new token: the page gets the node's own 401
          catchError(() => throwError(() => error))
        );
      })
    );
  }

  /** The service whose token to renew, when this is a first 401 from its node with its token. */
  private renewable(req: HttpRequest<any>, error: unknown): string | null {
    if (!(error instanceof HttpErrorResponse) || error.status !== 401 || req.context.get(SERVICE_TOKEN_RETRIED)) {
      return null;
    }
    if (req.url.startsWith(environment.RH_BASE_URL) || !isServiceUrl(req.url)) {
      return null;
    }

    // every service host starts with the service id: c0ffee.ulabase.app, c0ffee.eu-central-1-shared-1…
    const srvId = new URL(req.url).hostname.split('.')[0];
    const held = this.injector.get(SrvsService).cachedAdminJwt(srvId);
    return held && req.headers.get('Authorization') === `Bearer ${held}` ? srvId : null;
  }
}
